import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Input } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorCode, apiErrorMessage } from '../api/client';
import { operationRequestsApi } from '../api/endpoints';
import { invalidateMoneyData } from '../api/queryKeys';
import type { BlockedApproval } from '../api/types';
import { formatKopeks } from '../lib/money';

/** Batch key for a page-level «Утвердить все» (vs one per card/cell). */
export const ALL_KEY = '__all__';

export interface ApprovalActions {
  /** Approve one request, running the duplicate-confirm dance on 409. */
  approve: (id: string) => void;
  /** Same, awaitable — for chaining after an edit. */
  approveAsync: (id: string) => Promise<void>;
  approveBatch: (key: string, ids: string[]) => void;
  /** Ask for an optional reason, then reject. */
  confirmReject: (req: { id: string }) => void;
  /** Id of the row a single-row action is running on, or null. */
  busyId: string | null;
  /** Key of the batch currently running, or null. */
  busyGroup: string | null;
  approvePending: boolean;
  rejectPending: boolean;
  batchBusy: boolean;
}

/**
 * The admin's approve/reject vocabulary, shared by the approvals queue and the
 * jadval cell popover: the DUPLICATE_OPERATION_EXISTS → confirm →
 * re-approve-with-confirmDuplicate dance, the blocked-batch dialog and the
 * reject-with-note prompt. One place, one behaviour — this is real money, and
 * two copies of the guard would eventually disagree.
 *
 * Every action invalidates the whole money surface on success, so the jadval,
 * the queue and the nav badge move together.
 */
export function useApprovalActions(): ApprovalActions {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyGroup, setBusyGroup] = useState<string | null>(null);

  const invalidate = (): void => invalidateMoneyData(queryClient);

  /** A row someone else approved/rejected meanwhile reads better than the raw
   * server code — the refresh below makes it disappear either way. */
  const failed = (error: unknown): void => {
    if (apiErrorCode(error) === 'REQUEST_NOT_PENDING') {
      message.warning(t('approvals.notPending'));
      invalidate();
      return;
    }
    message.error(apiErrorMessage(error) ?? t('common.requestError'));
  };

  const approveMutation = useMutation({
    mutationFn: (input: { id: string; confirmDuplicate?: boolean }) =>
      operationRequestsApi.approve(input.id, input.confirmDuplicate ?? false),
    onMutate: (input) => setBusyId(input.id),
    onSuccess: () => {
      message.success(t('approvals.approved'));
      invalidate();
    },
    onError: (error, input) => {
      // The money guard fired: an identical ledger row already exists. Never
      // silently override — make the admin say so.
      if (apiErrorCode(error) === 'DUPLICATE_OPERATION_EXISTS') {
        const data = (error as { response?: { data?: { amount?: number; date?: string } } })
          .response?.data;
        modal.confirm({
          title: t('approvals.duplicateLedgerTag'),
          content: t('approvals.duplicateOperationConfirm', {
            amount: formatKopeks(data?.amount ?? 0),
            date: data?.date ? dayjs(data.date).format('DD.MM.YYYY') : '—',
          }),
          okText: t('approvals.approveAnyway'),
          okButtonProps: { danger: true },
          cancelText: t('common.cancel'),
          onOk: () =>
            approveMutation
              .mutateAsync({ id: input.id, confirmDuplicate: true })
              .then(() => undefined),
        });
        return;
      }
      failed(error);
    },
    onSettled: () => setBusyId(null),
  });

  const approveBatchMutation = useMutation({
    mutationFn: (input: { key: string; ids: string[]; confirmDuplicateIds?: string[] }) =>
      operationRequestsApi.approveBatch(input.ids, input.confirmDuplicateIds),
    onMutate: (input) => setBusyGroup(input.key),
    onSuccess: (result, input) => {
      message.success(t('approvals.approvedCount', { n: result.approved }));
      // Rows someone else already handled meanwhile — worth a heads-up.
      if (result.skipped > 0) message.warning(t('approvals.skippedCount', { n: result.skipped }));
      invalidate();
      // Look-alikes were left PENDING on purpose: show exactly what and why,
      // and make overriding a separate, deliberate action.
      if (result.blocked.length > 0) showBlocked(input.key, result.blocked);
    },
    onError: (error) => failed(error),
    onSettled: () => setBusyGroup(null),
  });

  const showBlocked = (key: string, blocked: BlockedApproval[]): void => {
    const ids = blocked.map((b) => b.requestId);
    modal.confirm({
      title: t('approvals.duplicateBlockedTitle'),
      width: 520,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>
            {t('approvals.duplicateBlockedBody', { n: blocked.length })}
          </div>
          {blocked.map((b) => (
            <div key={b.requestId} style={{ fontSize: 13 }}>
              {t('approvals.duplicateBlockedItem', {
                date: dayjs(b.date).format('DD.MM.YYYY'),
                store: b.storeName ?? '—',
                amount: formatKopeks(b.amount),
                reason: t(`approvals.duplicateReason.${b.reason}`),
              })}
            </div>
          ))}
        </div>
      ),
      okText: t('approvals.approveAnyway'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      // Overriding is a SECOND call naming the ids explicitly — the batch route
      // can never double money on its own.
      onOk: () =>
        approveBatchMutation
          .mutateAsync({ key, ids, confirmDuplicateIds: ids })
          .then(() => undefined),
    });
  };

  const rejectMutation = useMutation({
    mutationFn: (input: { id: string; note?: string }) =>
      operationRequestsApi.reject(input.id, input.note),
    onMutate: (input) => setBusyId(input.id),
    onSuccess: () => {
      message.success(t('approvals.rejected'));
      invalidate();
    },
    onError: (error) => failed(error),
    onSettled: () => setBusyId(null),
  });

  const confirmReject = (req: { id: string }): void => {
    let note = '';
    modal.confirm({
      title: t('approvals.rejectTitle'),
      content: (
        <Input.TextArea
          rows={2}
          placeholder={t('approvals.rejectNotePlaceholder')}
          onChange={(e) => (note = e.target.value)}
          maxLength={500}
        />
      ),
      okText: t('approvals.reject'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: () =>
        rejectMutation
          .mutateAsync({ id: req.id, note: note.trim() || undefined })
          .then(() => undefined),
    });
  };

  return {
    approve: (id) => approveMutation.mutate({ id }),
    approveAsync: (id) => approveMutation.mutateAsync({ id }).then(() => undefined),
    approveBatch: (key, ids) => approveBatchMutation.mutate({ key, ids }),
    confirmReject,
    busyId,
    busyGroup,
    approvePending: approveMutation.isPending,
    rejectPending: rejectMutation.isPending,
    batchBusy: approveBatchMutation.isPending,
  };
}
